import {
    AfterViewInit,
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    Type,
    ViewChild,
    ViewContainerRef,
    OnDestroy,
} from '@angular/core';
import {Subscription} from 'rxjs';
import {
    SearchFilterDefinition,
    SearchFilterSelection,
    SearchFilterDefaultValue,
    SearchFilterValue,
} from '../../filter-state.models';
import {SearchFilterBarComponentContract} from '../search-filter-bar.contract';

@Component({
    selector: 'app-configured-filter-bar-host',
    template: '<ng-template #host></ng-template>',
    standalone: false,
})
export class ConfiguredFilterBarHostComponent implements AfterViewInit, OnChanges, OnDestroy {
    @Input() public componentType: Type<SearchFilterBarComponentContract> | null = null;
    @Input() public definitions: readonly SearchFilterDefinition<SearchFilterValue>[] = [];
    @Input() public initialSelection: SearchFilterSelection<SearchFilterValue> = {};
    @Input() public defaultValues: Record<string, SearchFilterDefaultValue<SearchFilterValue>> = {};
    @Input() public resultCount: number | null = null;
    @Output() public selectionChange = new EventEmitter<SearchFilterSelection<SearchFilterValue>>();

    @ViewChild('host', {read: ViewContainerRef, static: true}) private host: ViewContainerRef;

    private componentRef: ComponentRef<SearchFilterBarComponentContract>;
    private appliedInitialSelection: SearchFilterSelection<SearchFilterValue> = {};
    private selectionChangeSubscription: Subscription;

    public ngOnDestroy(): void {
        this.selectionChangeSubscription?.unsubscribe();
    }

    public ngAfterViewInit(): void {
        this.renderComponent();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.componentRef && changes['componentType']) {
            this.renderComponent();
            return;
        }

        if (this.componentRef && changes['definitions']) {
            this.componentRef.setInput('definitions', this.definitions);
        }
        if (this.componentRef && changes['initialSelection']) {
            if (!this.sameSelection(this.appliedInitialSelection, this.initialSelection)) {
                this.componentRef.setInput('initialSelection', this.initialSelection);
                this.appliedInitialSelection = {...this.initialSelection};
            }
        }
        if (this.componentRef && changes['defaultValues']) {
            this.componentRef.setInput('defaultValues', this.defaultValues);
        }
        if (this.componentRef && changes['resultCount']) {
            this.componentRef.setInput('resultCount', this.resultCount);
        }
    }

    private renderComponent(): void {
        const componentType = this.componentType;
        if (!this.host || !componentType) {
            return;
        }

        this.host.clear();
        this.selectionChangeSubscription?.unsubscribe();
        this.componentRef = this.host.createComponent(componentType);
        this.componentRef.setInput('definitions', this.definitions);
        this.componentRef.setInput('initialSelection', this.initialSelection);
        this.componentRef.setInput('defaultValues', this.defaultValues);
        this.componentRef.setInput('resultCount', this.resultCount);
        this.appliedInitialSelection = {...this.initialSelection};
        this.selectionChangeSubscription = this.componentRef.instance.selectionChange
            .subscribe(selection => this.selectionChange.emit(selection));
    }

    private sameSelection(
        left: SearchFilterSelection<SearchFilterValue>,
        right: SearchFilterSelection<SearchFilterValue>,
    ): boolean {
        const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
        return [...keys].every(key => this.sameValue(left[key], right[key]));
    }

    private sameValue(left: unknown, right: unknown): boolean {
        if (Array.isArray(left) || Array.isArray(right)) {
            const leftValues = Array.isArray(left) ? left : [left];
            const rightValues = Array.isArray(right) ? right : [right];
            return leftValues.map(String).sort().join(',') === rightValues.map(String).sort().join(',');
        }

        return left === right;
    }
}
